"use client";

/**
 * Editor do conjunto de lojas de uma campanha existente (PRO013).
 *
 * Seção embutida nos modais/wizard de edição (NÃO é um dialog aninhado —
 * useDialogModal é singleton Redux, um modal novo substituiria o de edição).
 *
 * Regras:
 * - Campanha global (todas as lojas / 999): edição BLOQUEADA com mensagem explicativa.
 * - Campanha EXPIRADA: seção oculta (modal já bloqueia edição).
 * - ATIVA/FUTURA: seleção via StoreSelector (sem opção "todas as lojas"),
 *   preview do diff (+adicionadas / −removidas) e botão próprio "Salvar lojas".
 * - Salvar envia o conjunto COMPLETO via PATCH /campaigns/{tipo}/{code}/stores;
 *   409 abre o CampaignConflictModal com os conflitos das lojas adicionadas.
 */

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "components/ui/use-toast";
import {
  Store,
  Globe,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useUpdatePro013CampaignStoresMutation } from "@/services/campaign";
import { StoreSelector } from "../../create/components/shared/store-selector";
import { getTranslatedErrorMessage } from "../../utils/error-messages";
import { NO_CHANGES_TOAST } from "../../utils/messages";
import {
  CampaignConflictModal,
  type CampaignConflictItem,
} from "../campaign-conflict-modal";

export const GLOBAL_CAMPAIGN_STORES_MESSAGE =
  "Não é possível editar lojas em campanhas globais";

export const GLOBAL_CAMPAIGN_STORES_ORIENTATION =
  "Campanhas globais aplicam o desconto automaticamente em todas as lojas. " +
  "Para limitar a lojas específicas, encerre esta campanha e crie uma nova " +
  "selecionando as lojas desejadas.";

interface CampaignStoresEditorProps {
  type: "PRO013";
  campaignCode: number;
  /** Lojas atuais da campanha — aceita number[] ou objetos com code/CODE. */
  currentStores: Array<number | { code?: number; CODE?: number }>;
  allStores: boolean;
  status: string;
}

/** Bloco de bloqueio para campanhas globais — também usado no modal PRO017. */
export function GlobalCampaignStoresBlock() {
  return (
    <Alert className="border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20">
      <Globe className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-900 dark:text-amber-300">
        {GLOBAL_CAMPAIGN_STORES_MESSAGE}
      </AlertTitle>
      <AlertDescription className="text-amber-800 dark:text-amber-400">
        {GLOBAL_CAMPAIGN_STORES_ORIENTATION}
      </AlertDescription>
    </Alert>
  );
}

const SAVE_SUCCESS_TOAST = (added: number, removed: number) => ({
  variant: "default" as const,
  description:
    `Lojas atualizadas: ${added} adicionada(s), ${removed} removida(s). ` +
    "A propagação para as lojas ocorre na janela noturna (23:59).",
  duration: 6000,
});

export function CampaignStoresEditor({
  type,
  campaignCode,
  currentStores,
  allStores,
  status,
}: CampaignStoresEditorProps) {
  const { toast } = useToast();

  const { normalizedCurrent, hasGlobalSentinel } = useMemo(() => {
    const codes = (currentStores || [])
      .map(s => (typeof s === "number" ? s : Number(s?.code ?? s?.CODE)))
      .filter(code => !Number.isNaN(code));
    return {
      normalizedCurrent: codes.filter(code => code !== 999),
      // Defesa em profundidade: callers que recebem a lista crua do findByCode
      // (wizard) têm o 999 na lista em vez da flag allStores do unified.
      hasGlobalSentinel: codes.includes(999),
    };
  }, [currentStores]);

  const isGlobal = allStores || hasGlobalSentinel;

  const [open, setOpen] = useState(false);
  const [selectedStores, setSelectedStores] =
    useState<number[]>(normalizedCurrent);
  const [validationError, setValidationError] = useState<string>("");
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Re-sincroniza quando a campanha (re)carrega — ex.: refetch pós-save.
  useEffect(() => {
    setSelectedStores(normalizedCurrent);
  }, [normalizedCurrent]);

  const [updatePro013Stores, { isLoading: isSaving }] =
    useUpdatePro013CampaignStoresMutation();

  const mutationByType = {
    PRO013: updatePro013Stores,
  } as const;

  const diff = useMemo(() => {
    const currentSet = new Set(normalizedCurrent);
    const selectedSet = new Set(selectedStores);
    return {
      added: selectedStores.filter(code => !currentSet.has(code)),
      removed: normalizedCurrent.filter(code => !selectedSet.has(code)),
    };
  }, [normalizedCurrent, selectedStores]);

  const hasChanges = diff.added.length > 0 || diff.removed.length > 0;

  if (status === "EXPIRED") return null;

  if (isGlobal) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Store className="h-4 w-4" />
          <span className="text-sm font-medium">Lojas da campanha</span>
        </div>
        <GlobalCampaignStoresBlock />
      </div>
    );
  }

  const handleSave = async () => {
    if (selectedStores.length === 0) {
      setValidationError("Selecione pelo menos uma loja");
      return;
    }
    if (!hasChanges) {
      toast(NO_CHANGES_TOAST);
      return;
    }

    try {
      const result = await mutationByType[type]({
        code: campaignCode,
        stores: selectedStores,
      }).unwrap();

      toast(
        SAVE_SUCCESS_TOAST(
          result.data?.added?.length ?? diff.added.length,
          result.data?.removed?.length ?? diff.removed.length,
        ),
      );
    } catch (err: unknown) {
      const typedErr = err as {
        status?: number;
        data?: { message?: string; conflicts?: CampaignConflictItem[] };
      };

      if (typedErr?.status === 409 && typedErr?.data?.conflicts) {
        setConflicts(typedErr.data.conflicts);
        setShowConflictModal(true);
        return;
      }

      const errorMessage = getTranslatedErrorMessage(
        typedErr,
        "Erro ao atualizar as lojas da campanha. Tente novamente.",
      );
      toast({
        variant: "destructive",
        description: errorMessage,
        duration: 6000,
      });
    }
  };

  return (
    <div className="space-y-3 border rounded-lg p-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <Store className="h-4 w-4" />
          <span className="text-sm font-medium">
            Lojas da campanha ({normalizedCurrent.length})
          </span>
        </div>
        {hasChanges && (
          <Badge variant="secondary" className="text-xs">
            alterações pendentes
          </Badge>
        )}
      </button>

      {open && (
        <div className="space-y-3">
          <StoreSelector
            allStores={false}
            selectedStores={selectedStores}
            updateData={data => {
              if (data.stores !== undefined) setSelectedStores(data.stores);
            }}
            error={validationError}
            clearError={() => setValidationError("")}
            hideAllStoresOption
          />

          {/* Preview do diff */}
          {hasChanges && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {diff.added.length > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                    +{diff.added.length} adicionada(s)
                  </Badge>
                )}
                {diff.removed.length > 0 && (
                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                    −{diff.removed.length} removida(s)
                  </Badge>
                )}
              </div>
              {diff.added.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Adicionar:{" "}
                  {diff.added.map(code => (
                    <Badge key={code} variant="outline" className="mr-1 font-mono text-xs">
                      {code}
                    </Badge>
                  ))}
                </p>
              )}
              {diff.removed.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground">
                    Remover:{" "}
                    {diff.removed.map(code => (
                      <Badge key={code} variant="outline" className="mr-1 font-mono text-xs">
                        {code}
                      </Badge>
                    ))}
                  </p>
                  <p className="flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    Lojas removidas deixam de aplicar o desconto — o registro da
                    campanha será expirado no banco da loja na janela noturna.
                  </p>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar lojas
            </Button>
          </div>
        </div>
      )}

      <CampaignConflictModal
        isOpen={showConflictModal}
        conflicts={conflicts}
        onClose={() => {
          setShowConflictModal(false);
          setConflicts([]);
        }}
        onGoBack={() => {
          setShowConflictModal(false);
          setConflicts([]);
        }}
      />
    </div>
  );
}
